import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface Customer {
  id?: number;
  customer_id?: string;
  email?: string;
  firstname?: string;
  lastname?: string;
  gender_id?: number;
  city_id?: number;
  city?: string;
  address?: string;
  zipcode?: string;
  contact?: string;
  requirement_id?: number;
  purpose_id?: number;
  company_name?: string;
  profession_id?: number;
  designation_id?: number;
  job_description?: string;
  user_id?: number;
  status?: number;
}

export interface DropdownItem {
  id: number;
  name: string;
  display_name?: string;
  status?: number;
}

@Injectable({ providedIn: 'root' })
export class AccountService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient, private auth: AuthService) {}

  private authHeaders(): HttpHeaders {
    const token = this.auth.token;
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    });
  }

  // ── Customer Profile ──────────────────────────────────────────

  getCustomerProfile(): Observable<any> {
    const userId = this.auth.currentUser?.id || 0;
    const filter = JSON.stringify([['user_id', '=', userId]]);
    return this.http.get(`${this.apiUrl}/api/auth/customer`, {
      headers: this.authHeaders(),
      params: { filter, pageNumber: '0', pageSize: '-1', orderBy: '[]' }
    });
  }

  updateCustomerProfile(customer: Customer): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/auth/customer`, customer, {
      headers: this.authHeaders()
    });
  }

  // ── Dropdown Data ─────────────────────────────────────────────

  getGenders(): Observable<any> {
    const filter = JSON.stringify([['status', '=', 1]]);
    return this.http.get(`${this.apiUrl}/api/unauth/gender`, {
      params: { filter, pageNumber: '0', pageSize: '-1', orderBy: '[]' }
    });
  }

  getRequirements(): Observable<any> {
    const filter = JSON.stringify([['status', '=', 1]]);
    return this.http.get(`${this.apiUrl}/api/unauth/requirement`, {
      params: { filter, pageNumber: '0', pageSize: '-1', orderBy: '[]' }
    });
  }

  getPurposes(): Observable<any> {
    const filter = JSON.stringify([['status', '=', 1]]);
    return this.http.get(`${this.apiUrl}/api/unauth/purpose`, {
      params: { filter, pageNumber: '0', pageSize: '-1', orderBy: '[]' }
    });
  }

  searchCities(query: string): Observable<any> {
    const filter = JSON.stringify([['search_key', query]]);
    return this.http.get(`${this.apiUrl}/api/unauth/city`, {
      params: { filter, pageNumber: '0' }
    });
  }

  // ── Orders ────────────────────────────────────────────────────

  /**
   * The signed-in customer's OWN orders, newest first (GET /api/site/orders).
   * Scoped server-side to their account; `stage` = all | active | completed | cancelled.
   */
  getOrders(stage: string = 'all', page = 1, perPage = 10): Observable<any> {
    return this.http.get(`${environment.newApiUrl}/api/site/orders`, {
      headers: this.authHeaders(),
      // pending is the dashboard's old word for orders still in progress.
      params: { stage: stage === 'pending' ? 'active' : stage, page: String(page), per_page: String(perPage) },
    });
  }

  /**
   * One of the customer's own orders in full — lines, the invoice's price
   * breakdown, addresses, payments and shipment (GET /api/site/orders/{ref}).
   * Answers 404 for an order that is not theirs. It replaced the old
   * unauthenticated /api/unauth/orderById, which showed any order to anyone.
   */
  getOrderById(orderId: string): Observable<any> {
    return this.http.get(`${environment.newApiUrl}/api/site/orders/${encodeURIComponent(orderId)}`, {
      headers: this.authHeaders(),
    });
  }
}